"use client";

import { useCallback, useMemo } from "react";
import { useOriginActions } from "@/features/origins";
import { useUserChoices } from "@/state/globalStore";
import useConfirmationDialog from "@/shared/ui/useConfirmationDialog";
import type {
  DeleteOriginsRequest,
  Origin,
  PutOriginRequest,
} from "@/generated/fastapi";
import useBacklogSearchSync from "./useBacklogSearchSync";

export default function useBacklogOriginActions() {
  const confirmOriginDeletion = useConfirmationDialog();
  const { resetSelectedOrigins, getSelectedOriginIds } = useUserChoices();
  const { syncBacklogOriginDeleted, syncBacklogOriginUpdated } =
    useBacklogSearchSync();
  const {
    handleDeleteOriginsClick,
    handleUpdateOriginClick: handleUpdateOriginBase,
    handleCopyRawTextInputToClipboard,
  } = useOriginActions();

  const handleSelectedOriginsDeleteClick = useCallback(
    async (request: DeleteOriginsRequest, onSuccess?: () => void) => {
      const executeDelete = async () => {
        const result = await handleDeleteOriginsClick(request, () => {
          resetSelectedOrigins();
          onSuccess?.();
        });
        syncBacklogOriginDeleted(result);
      };

      confirmOriginDeletion.onOpen(executeDelete);
    },
    [
      confirmOriginDeletion,
      handleDeleteOriginsClick,
      resetSelectedOrigins,
      syncBacklogOriginDeleted,
    ],
  );

  const handleSelectedOriginsUnselectClick = useCallback(async () => {
    resetSelectedOrigins();
  }, [resetSelectedOrigins]);

  const handleUpdateOriginClick = useCallback(
    async (
      request: PutOriginRequest,
      onSuccess?: (updatedOrigin: Origin) => Promise<Origin | null> | Origin,
    ): Promise<Origin | null> => {
      const updatedOrigin = await handleUpdateOriginBase(request, onSuccess);
      if (updatedOrigin) {
        syncBacklogOriginUpdated();
      }
      return updatedOrigin;
    },
    [handleUpdateOriginBase, syncBacklogOriginUpdated],
  );

  return useMemo(
    () => ({
      confirmOriginDeletion,
      getSelectedOriginIds,
      handleSelectedOriginsDeleteClick,
      handleSelectedOriginsUnselectClick,
      handleUpdateOriginClick,
      handleCopyRawTextInputToClipboard,
    }),
    [
      confirmOriginDeletion,
      getSelectedOriginIds,
      handleSelectedOriginsDeleteClick,
      handleSelectedOriginsUnselectClick,
      handleUpdateOriginClick,
      handleCopyRawTextInputToClipboard,
    ],
  );
}
